// OneDrive:IgnoreCodeCoverage

import { Engagement as EngagementEvent } from './events/Engagement.event';
import { Experiment } from './events/Experiment.event';
import { ExperimentTypeEnum } from './events/ExperimentTypeEnum';
import { ISpPageContext } from '../dataSources/ISpPageContext';
import { Killswitch } from '../killswitch/Killswitch';
import { deserializeQuery } from '../navigation/AddressParser';
import { IFeature } from '../features/Features';
import ErrorHelper from './ErrorHelper';
import FeatureOverrides from '../features/FeatureOverrides';
import * as  murmurhash from 'murmurhash';

/**
 * @file Contains components to perform AB testing experiments.
 */

/**
 * Represents data that specifies the settings for an AB Experiment.
 * @example
 * // set the experiment data.
 * let newMenuExperimentData: IABExperimentData = {
 *     name: "NewMenuExperiment",
 *     startDate: "2019/06/01",
 *     version: ABExpHash.v2
 * };.
 */
export interface IABExperimentData {
    /**
     * The name of the experiment. Only supports alphanumeric characters,
     * no spaces or other special characters.
     */
    name: string;
    /**
     * The date when the AB Testing experiment will be created.
     * i.e. "10/12/2016"; used for tracking purposes.
     */
    startDate: string;
    /**
     * DEPRECATED: Only the default value (0.5 / 50%) should be used.
     * The percentage of population that will receive the experiment.
     * If not provided, it defaults to 0.5 (meaning 50%).
     */
    segmentPopulation?: number;
    /**
     * The type of target used for running the experiment. Only the values of user (default) or
     * site should be used.
     */
    targetType?: TargetType;
    /**
     * REQUIRED: All new experiments should specify ABExpHash.v2. The default
     * value of v1 is only maintained for backwards compatibility to prevent
     * reassignment for existing experiments.
     * The hash algorithm used for assignment.
     */
    version?: ABExpHash;
    /**
     * Site ID of the TeamSite
     */
    siteId?: string;
}

/**
 * Enum for the types of targets that can be used to run an experiment.
 * Only the values of user (default) or site should be used. Other types are maintained
 * for backwards compatibility.
 */
export enum TargetType {
    user = 1,
    tenant = 2,
    farm = 3,
    site = 4
}

/**
 * Enum for the types of algorithms used for assignment. Only v2 should be used for all new experiments.
 */
export enum ABExpHash {
    /**
     * Original implementation using DJB2 hash, and using user or site ID, farm label and site subscription ID as hash input.
     * No new experiments should be created using this hash implementation.
     */
    v1 = 1,
    /** New implementation using Murmur hash, and only user or site id as hash input. All new experiments should use this hash function. */
    v2 = 2
}

/**
 * This class will be used for AB Testing type of experiments.
 *
 * You can force a single experiment on or off using query params:
 *   `enableExp=MyExperimentName` or `disableExp=MyExperimentName`
 *
 * (Note: even if an experiment is forced on/off, the actual experiment state for the given target
 * will be logged, rather than the forced state.)
 */
export class ABExperiment {
    private static _pageContext: ISpPageContext;
    private _experimentData: IABExperimentData;
    private _isOn: boolean;

    /**
     * @param experimentData The data for the experiment. e.g.
     * ```
     * { name: "ExperimentName", startData: "01/01/2018", version: ABExpHash.v2 }.
     * ```
     * Note: property `name` should only contain alphanumeric characters (no spaces or other
     * special characters). Property `version` must be specified as `ABExpHash.v2` for all new experiments.
     * @param aaToABFeature If provided, the experiment will initially run as an A/A experiment
     * under the given `name` plus "AA" at the end (e.g. "MyExperimentAA"). When the feature
     * is enabled, the experiment will convert to an A/B experiment using the original `name`.
     * (In an "A/A experiment," data is logged to help ensure everything is working correctly, but
     * everyone gets the old experience.)
     *
     * @example
     * // set the experiment data
     * let newMenuExperimentData: IABExperimentData = { name: "NewMenuExperiment", startDate: "2019/06/01", version: ABExpHash.v2 };
     * // create the experiment object
     * let newMenuExperiment: ABExperiment = new ABExperiment(newMenuExperimentData);
     */
    constructor(experimentData: IABExperimentData, aaToABFeature?: IFeature) {
        const originalName = experimentData.name;
        const isAAExperiment = aaToABFeature && !FeatureOverrides.isFeatureEnabled(aaToABFeature);

        if (!Killswitch.isActivated('ecfe2fe1-07c6-4117-98d8-27582d0384f7', '10/25/2018') && !this._isValidName(experimentData.name)) {
            ErrorHelper.log(`Experiment name '${experimentData.name}' is invalid.`);
        }

        this._experimentData = experimentData = {
            segmentPopulation: 0.5,
            targetType: TargetType.user,
            version: ABExpHash.v1,
            ...experimentData
        };
        if (isAAExperiment) {
            experimentData.name += 'AA';
        }

        this._isOn = false;

        if (!ABExperiment._pageContext) {
            // log that the App that wants to do experiments
            // does not have host settings and quit doing the experiment checks
            EngagementEvent.logData({ name: this._experimentData.name + ".Experiment.HostSettingsMissing" });
        } else {
            // set the isOn flag based on the target type
            // if it ever reaches the default then return false
            switch (this._experimentData.targetType) {
                case TargetType.user:
                    this._isOn = this._isUserExperimentOn();
                    break;
                case TargetType.tenant:
                    this._isOn = this._isTenantExperimentOn();
                    break;
                case TargetType.farm:
                    this._isOn = this._isFarmExperimentOn();
                    break;
                case TargetType.site:
                    this._isOn = this._isSiteExperimentOn();
                    break;
            }
        }

        // If we're in A/A experiment stage, everyone gets the old experience
        // (the checks above are still needed so we log which population segment the target is in)
        if (isAAExperiment) {
            this._isOn = false;
        }

        // Check whether the experiment has been forced on or off
        // (do this after the checks above so it doesn't affect logging)
        let viewParams = deserializeQuery((window.location.search || '').substr(1));
        let enabledExps = (viewParams['enableExp'] || '').split(',');
        let disabledExps = (viewParams['disableExp'] || '').split(',');
        if (enabledExps.indexOf(originalName) !== -1) {
            this._isOn = true;
        } else if (disabledExps.indexOf(originalName) !== -1) {
            this._isOn = false;
        }
    }

    /**
     * Initializes ABExperiment with the pageContext.
     * @param pageContext The _spPageContextInfo of the app where is experiment is running.
     */
    public static init(pageContext: ISpPageContext): void {
        ABExperiment._pageContext = {
            ...pageContext,
            userLoginName: pageContext.userLoginName || 'missing_user',
            siteSubscriptionId: pageContext.siteSubscriptionId || 'missing_tenant',
            farmLabel: pageContext.farmLabel || 'missing_farm',
            siteId: pageContext.siteId || 'missing_site'
        };
    }

    /** @deprecated Please use `init` */
    public static Init(pageContext: ISpPageContext): void {
        ABExperiment.init(pageContext);
    }

    /**
     * Returns the experiment data to be used for logging purposes.
     */
    public getExperimentData(): IABExperimentData {
        return this._experimentData;
    }

    /**
     * Checks if the current target is selected to be part of the experiment.
     */
    public isOn(): boolean {
        return this._isOn;
    }

    /**
     * Checks if the current target is selected to be part of the experiment.
     * @deprecated Use `isOn`
     */
    public IsExperimentOn(): boolean {
        return this._isOn;
    }

    /**
     * Checks if the current user is being selected to be part of the experiment.
     */
    private _isUserExperimentOn(): boolean {
        // in case there are no host settings default to no experiments
        const pageContext = ABExperiment._pageContext;
        if (!pageContext) {
            return false;
        }

        let targetToken = '';
        const { name: experimentName, version } = this._experimentData;
        if (version === ABExpHash.v1) {
            targetToken = experimentName + '_' +
                pageContext.userLoginName + '_' +
                pageContext.siteSubscriptionId + '_' +
                pageContext.farmLabel;
        } else if (version === ABExpHash.v2) {
            targetToken = experimentName + pageContext.userLoginName;
        }

        return this._isExperimentOn(targetToken);
    }

    /**
     * Checks if the current tenant is being selected to be part of the experiment.
     */
    private _isTenantExperimentOn(): boolean {
        // in case there are no settings default to no experiment path
        const pageContext = ABExperiment._pageContext;
        if (!pageContext) {
            return false;
        }
        let targetToken = '';
        const { name: experimentName, version } = this._experimentData;
        if (version === ABExpHash.v1) {
            targetToken = experimentName + '_' +
                pageContext.siteSubscriptionId + '_' +
                pageContext.farmLabel;
        } else if (version === ABExpHash.v2) {
            targetToken = experimentName + pageContext.siteSubscriptionId;
        }

        return this._isExperimentOn(targetToken);
    }

    /**
     * Checks if the current farm is being selected to be part of the experiment.
     */
    private _isFarmExperimentOn(): boolean {
        // in case there are no settings default to no experiment path
        const pageContext = ABExperiment._pageContext;
        if (!pageContext) {
            return false;
        }

        let targetToken = '';
        const { name: experimentName, version } = this._experimentData;
        if (version === ABExpHash.v1) {
            targetToken = experimentName + '_' +
                this._getSiteId() + '_' +
                pageContext.siteSubscriptionId + '_' +
                pageContext.farmLabel;
        } else if (version === ABExpHash.v2) {
            targetToken = experimentName + this._getSiteId();
        }
        return this._isExperimentOn(targetToken);
    }

    /**
     * Checks if the current site in the current tenant is being selected to be part of the experiment.
     */
    private _isSiteExperimentOn(): boolean {
        // in case there are no settings default to no experiment path
        const pageContext = ABExperiment._pageContext;
        if (!pageContext) {
            return false;
        }

        // build the token using siteId and SiteSubscriptionId
        // since siteId is unique inside a tenant
        let targetToken = this._experimentData.name + '_' +
            this._getSiteId() + '_' +
            pageContext.siteSubscriptionId + '_' +
            pageContext.farmLabel;
        return this._isExperimentOn(targetToken);
    }

    /**
     * Ensures the experiment data passed is valid. For now this means making sure the name only
     * contains alphanumeric characters (no spaces or underscores).
     *
     * @param experimentData experiment data passed to the constructor
     */
    private _isValidName(experimentName: string): boolean {
        return !/[^A-Za-z0-9]/.test(experimentName);
    }

    /**
     * Returns the siteId if it is passed when ABExperiment is instantiated, else
     * returns the siteId present in the page context
     */
    private _getSiteId(): string {
        const pageContext = ABExperiment._pageContext;
        return !!this._experimentData.siteId ? this._experimentData.siteId : pageContext.siteId;
    }

    /**
     * Returns true if the curent target type is being selected to be part of the experiment.
     * @param targetToken this is the unique identfier for target.
     * @returns true if the targetToken is in the segementPopulation defined for the experiment.
     */
    private _isExperimentOn(targetToken: string): boolean {
        if (!targetToken) {
            return false;
        }

        // get the hash number for the specified targetToken
        let varHash = 0;
        if (this._experimentData.version === ABExpHash.v1) {
            varHash = this._getHashNumber(targetToken);
        } else if (this._experimentData.version === ABExpHash.v2) {
            varHash = this._getHashNumberMurmur(targetToken);
        }

        // it returns true if varHash is less than segementPopulation
        // same time, it logs experiment info
        let result = varHash <= this._experimentData.segmentPopulation;
        this._logData(result);
        return result;
    }

    /**
     * DEPRECATED: all new experiments should use `ABExpHash.v2` (see `_getHashNumberMurmur`).
     * This version of the hash function may result in biasing issues or sample ratio mismatch
     * and is maintained for backwards compatibility for preexisting experiments only.
     * Returns an unique number based on the string provided.
     * @param inputString this is the unique uber identfier for target.
     */
    private _getHashNumber(inputString: string): number {
        // in case there will be empty strings returns 1
        // this will take it to the experiment off path
        if (inputString.length === 0) {
            return 1;
        }

        let hash = 5381;
        for (var i = 0; i < inputString.length; ++i) {
            hash = ((hash << 5) + hash) + inputString.charCodeAt(i);
            hash = hash & hash;
        }

        hash = hash & 0xFFFF;
        return hash / 0xFFFF;
    }

    /**
     * Returns an unique number based on Murmur hash of string provided
     * @param inputString this is the unique uber identfier for target.
     */
    private _getHashNumberMurmur(inputString: string): number {
        return murmurhash.v3(inputString, 1) / 0xFFFFFFFF;
    }

    /**
     * Returns experiment type based on target type set
     */
    private _getExperimentType(): ExperimentTypeEnum {
        if (this._experimentData.targetType === TargetType.user) {
            return ExperimentTypeEnum.User;
        } else if (this._experimentData.targetType === TargetType.site) {
            return ExperimentTypeEnum.Site;
        } else {
            // only user and site level experiments are supported
            return ExperimentTypeEnum.Unknown;
        }
    }

    /**
     * Log the experiment data for testing purposes.
     */
    private _logData(isOn: boolean) {
        Experiment.logData({
            name: "",
            isInRamp: isOn,
            experiment: this._experimentData.name,
            variant: isOn.toString(),
            assignmentType: this._getExperimentType(),
            // site level experiments require logging assignment IDs because unlike user IDs, it is not automatically captured.
            assignmentId: this._experimentData.targetType === TargetType.site ? this._getSiteId() : ""
        });
    }
}
